import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, groupBy, map, mergeMap, switchMap } from 'rxjs/operators'

import { combineEpicsIsolated } from 'utils/epicRegistry'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_APP_DATA_ASYNC } from 'containers/App/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { addToast } from 'containers/Toasts/actions'

import {
  GET_ATTACK_GENERATION_DATA_ASYNC,
  GET_COMPANY_RISK_ASYNC,
  GET_COMPANY_RISK_TREND_ASYNC,
  GET_RED_TEAM_STATUS_ASYNC,
  GET_TRAINING_ACTIVITY_ASYNC,
  SET_TRAINING_ACTIVITY_PAGE,
  SUBMIT_ATTACK_FEEDBACK_ASYNC,
} from './constants'
import { selectTrainingActivityPage } from './selectors'

const TRAINING_ACTIVITY_PER_PAGE = 10
const selectCompanyId = (state) => selectCompanyFilter(state)?.value

const getErrorMessage = (error, fallbackMessage) =>
  error.response?.error?.message ||
  error.response?.data?.message ||
  error.response?.message ||
  error.response?.detail ||
  error.message ||
  fallbackMessage

const getRedTeamStatusEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      // FETCH_APP_DATA_ASYNC.SUCCESS,
      // FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
      GET_RED_TEAM_STATUS_ASYNC.INITIATE
    ),
    filter(() => matchURL(URL_CONST.redTeam.self)),
    switchMap(() => {
      const state = state$.value
      const companyId = selectCompanyId(state)
      return api.redTeam.getRedTeamStatus(companyId).pipe(
        map((response) => GET_RED_TEAM_STATUS_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(GET_RED_TEAM_STATUS_ASYNC.onError, error),
            addToast({
              message: getErrorMessage(error, 'Failed to load red team company profile.'),
              type: 'error',
            })
          )
        )
      )
    })
  )

const getAttackGenerationDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      // FETCH_APP_DATA_ASYNC.SUCCESS
      // FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
      GET_ATTACK_GENERATION_DATA_ASYNC.INITIATE
    ),
    filter(() => matchURL(URL_CONST.redTeam.self)),
    switchMap(() => {
      const state = state$.value
      const companyId = selectCompanyId(state)
      return api.redTeam.getAttackGenerationData(companyId).pipe(
        map((response) => GET_ATTACK_GENERATION_DATA_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(GET_ATTACK_GENERATION_DATA_ASYNC.onError, error),
            addToast({
              message: getErrorMessage(error, 'Failed to load attack generation data.'),
              type: 'error',
            })
          )
        )
      )
    })
  )

const getCompanyRiskEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_COMPANY_RISK_ASYNC.INITIATE),
    filter(() => matchURL(URL_CONST.redTeam.self)),
    switchMap(() => {
      const state = state$.value
      const companyId = selectCompanyId(state)
      return api.redTeam.getCompanyRisk(companyId).pipe(
        map((response) => GET_COMPANY_RISK_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(GET_COMPANY_RISK_ASYNC.onError, error),
            addToast({
              message: getErrorMessage(error, 'Failed to load risk metrics.'),
              type: 'error',
            })
          )
        )
      )
    })
  )

const getCompanyRiskTrendEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(GET_COMPANY_RISK_TREND_ASYNC.INITIATE),
    filter(() => matchURL(URL_CONST.redTeam.self)),
    switchMap(() => {
      const state = state$.value
      const companyId = selectCompanyId(state)
      return api.redTeam.getCompanyRiskTrend(companyId).pipe(
        map((response) => GET_COMPANY_RISK_TREND_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(GET_COMPANY_RISK_TREND_ASYNC.onError, error),
            addToast({
              message: getErrorMessage(error, 'Failed to load risk trend.'),
              type: 'error',
            })
          )
        )
      )
    })
  )

const getTrainingActivityEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      GET_TRAINING_ACTIVITY_ASYNC.INITIATE, // FETCH_APP_DATA_ASYNC.SUCCESS
      // FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
      SET_TRAINING_ACTIVITY_PAGE
    ),
    filter(() => matchURL(URL_CONST.redTeam.self)),
    switchMap(() => {
      const state = state$.value
      const companyId = selectCompanyId(state)
      const page = selectTrainingActivityPage(state)
      return api.redTeam.getTrainingActivity(companyId, page, TRAINING_ACTIVITY_PER_PAGE).pipe(
        map((response) => GET_TRAINING_ACTIVITY_ASYNC.onSuccess(response)),
        catchError((error) =>
          of(
            epicsErrorHandler(GET_TRAINING_ACTIVITY_ASYNC.onError, error),
            addToast({
              message: getErrorMessage(
                error,
                page > 0
                  ? 'Failed to load the selected training activity page.'
                  : 'Failed to load training activity.'
              ),
              type: 'error',
            })
          )
        )
      )
    })
  )

const submitAttackFeedbackEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(SUBMIT_ATTACK_FEEDBACK_ASYNC.INITIATE),
    groupBy((action) => action.payload?.attackEmailId),
    mergeMap((group$) =>
      group$.pipe(
        switchMap((action) => {
          const { storyId, attackEmailId, feedback, comment } = action.payload || {}
          const state = state$.value
          const companyId = selectCompanyId(state)
          return api.redTeam
            .submitAttackFeedback(storyId, attackEmailId, feedback, comment, companyId)
            .pipe(
              map(() => ({
                type: SUBMIT_ATTACK_FEEDBACK_ASYNC.SUCCESS,
                payload: { attackEmailId, feedback },
              })),
              catchError((error) =>
                of(
                  {
                    type: SUBMIT_ATTACK_FEEDBACK_ASYNC.ERROR,
                    payload: { attackEmailId },
                    error: {
                      title: 'Error',
                      detail: getErrorMessage(error, 'Failed to submit feedback.'),
                      status: error.status,
                      response: error.response,
                    },
                  },
                  addToast({
                    message: getErrorMessage(error, 'Failed to submit feedback.'),
                    type: 'error',
                  })
                )
              )
            )
        })
      )
    )
  )

export default combineEpicsIsolated(
  getRedTeamStatusEpic,
  getAttackGenerationDataEpic,
  getCompanyRiskEpic,
  getCompanyRiskTrendEpic,
  getTrainingActivityEpic,
  submitAttackFeedbackEpic
)
